import { Box, Flex, Grid, GridItem, Icon, Image, VStack } from "@chakra-ui/react";
import GradientButton from "@components/ui/GradientButton";
import { useWindowSiteStore } from "@store/windowSiteStore";
import { scale } from "@utils/helpers";
import { Fragment, useEffect, useState } from "react";
import { PaddedOnMobile } from "../layout";
import { motion } from "framer-motion";

const MotionGridItem = motion(GridItem);
const MotionFlex = motion(Flex);
const MotionImage = motion(Image);

const EasierSectionData = {
    label: "Easier",
    labelColor: "gold.500",
    title: {
        desktop: (
            <>
                Power without
                <br /> Complexity
            </>
        ),
        mobile: "Power without Complexity",
    },
    subtitle: "Advanced algorithm, effortless experience",
    bullets: [
        {
            icon: "/icons/filtering.svg",
            title: "Visual Metadata filtering",
            description:
                "Visualize each trait's distribution, rank and value across an entire collection.",
            comingSoon: false,
        },
        {
            icon: "/icons/wallet-compare.svg",
            title: "Wallet Compare",
            description: "Size up your NFTs against the comparison.",
            comingSoon: true,
        },
        {
            icon: "/icons/best-deals.svg",
            title: "Best Deals Across Collections",
            description:
                "Absolute highest discounts across the blockchain's best digital collectibles.",
            comingSoon: true,
        },
    ],
};

type EasierSectionProps = {};

export const EasierSection = ({}: EasierSectionProps) => {
    const isDesktop = useWindowSiteStore((state) => state.isDesktop);
    const windowWidth = useWindowSiteStore((state) => state.windowWidth);

    const minSize = isDesktop ? 960 : 375;
    const maxSize = isDesktop ? 1200 : 960;

    const easierImage = "/images/home/easier.png";

    const scaleAnimation = (delay = 0, once = true) => ({
        initial: {
            scale: 0.8,
            opacity: 0,
        },
        whileInView: {
            scale: 1,
            opacity: 1,
        },
        transition: {
            duration: 0.5,
            ease: "easeOut",
            delay: delay,
        },
        viewport: {
            once: once,
        },
    });

    const slideUpAnimation = (delay = 0, once = true) => ({
        initial: {
            translateY: "50px",
            opacity: 0,
        },
        whileInView: {
            translateY: "0px",
            opacity: 1,
        },
        transition: {
            duration: 0.8,
            ease: "easeOut",
            delay: delay,
        },
        viewport: {
            once: once,
        },
    });

    const slideRightAnimation = (delay = 0, once = true) => ({
        initial: {
            translateX: "-200px",
            opacity: 0,
        },
        whileInView: {
            translateX: "0px",
            opacity: 1,
        },
        transition: {
            duration: 0.8,
            ease: "easeOut",
            delay: delay,
        },
        viewport: {
            once: once,
        },
    });

    if (isDesktop) {
        return (
            <>
                <PaddedOnMobile>
                    <Grid w="100%" templateColumns="1fr 50%" gap={8}>
                        <GridItem>
                            <Flex direction="column" w="100%">
                                <Box
                                    fontFamily="regular"
                                    fontSize={scale(windowWidth, minSize, maxSize, 18, 24)}
                                    color={EasierSectionData.labelColor}
                                >
                                    {EasierSectionData.label}
                                </Box>
                                <Box
                                    fontFamily="regular"
                                    fontSize={scale(windowWidth, minSize, maxSize, 55, 72)}
                                    lineHeight="110%"
                                >
                                    {EasierSectionData.title.desktop}
                                </Box>
                                <Box
                                    pt={2}
                                    pb={6}
                                    fontSize={scale(windowWidth, minSize, maxSize, 20, 24)}
                                    fontWeight="light"
                                >
                                    {EasierSectionData.subtitle}
                                </Box>
                                <Grid
                                    w="100%"
                                    templateColumns="auto"
                                    templateRows="auto auto auto"
                                    rowGap={6}
                                >
                                    {EasierSectionData.bullets.map((bullet, index) => {
                                        return (
                                            <Fragment key={index}>
                                                <MotionGridItem {...slideRightAnimation(0.2)}>
                                                    <Grid templateColumns="auto 1fr" columnGap={3}>
                                                        <GridItem>
                                                            <Icon
                                                                as={Image}
                                                                src={bullet.icon}
                                                                w={
                                                                    scale(
                                                                        windowWidth,
                                                                        minSize,
                                                                        maxSize,
                                                                        70,
                                                                        85,
                                                                    ) + "px"
                                                                }
                                                                h={
                                                                    scale(
                                                                        windowWidth,
                                                                        minSize,
                                                                        maxSize,
                                                                        70,
                                                                        85,
                                                                    ) + "px"
                                                                }
                                                            />
                                                        </GridItem>
                                                        <GridItem>
                                                            <Flex
                                                                direction="column"
                                                                justify="center"
                                                                h="100%"
                                                                gap={1}
                                                            >
                                                                <Box
                                                                    fontFamily="regular"
                                                                    fontSize={scale(
                                                                        windowWidth,
                                                                        minSize,
                                                                        maxSize,
                                                                        24,
                                                                        30,
                                                                    )}
                                                                    lineHeight="110%"
                                                                >
                                                                    {bullet.title}
                                                                </Box>
                                                                <Box
                                                                    fontSize={scale(
                                                                        windowWidth,
                                                                        minSize,
                                                                        maxSize,
                                                                        18,
                                                                        24,
                                                                    )}
                                                                    fontWeight="light"
                                                                    color="lavenderGray"
                                                                    lineHeight="110%"
                                                                >
                                                                    {bullet.description}
                                                                </Box>
                                                                {bullet.comingSoon && (
                                                                    <Box
                                                                        fontFamily="regular"
                                                                        fontSize={scale(
                                                                            windowWidth,
                                                                            minSize,
                                                                            maxSize,
                                                                            10,
                                                                            15,
                                                                        )}
                                                                        fontWeight="medium"
                                                                        color="lavenderGray"
                                                                        lineHeight="110%"
                                                                    >
                                                                        Coming Soon
                                                                    </Box>
                                                                )}
                                                            </Flex>
                                                        </GridItem>
                                                    </Grid>
                                                </MotionGridItem>
                                            </Fragment>
                                        );
                                    })}
                                </Grid>
                                <MotionFlex
                                    w="100%"
                                    pt={8}
                                    justify="center"
                                    align="center"
                                    {...scaleAnimation(0.2)}
                                >
                                    <Box w="70%">
                                        <GradientButton
                                            link="/app"
                                            label="View App"
                                            // tag="BETA"
                                        />
                                    </Box>
                                </MotionFlex>
                            </Flex>
                        </GridItem>
                        <GridItem>
                            <Flex h="100%" align="start">
                                <MotionImage src={easierImage} {...scaleAnimation()} />
                            </Flex>
                        </GridItem>
                    </Grid>
                </PaddedOnMobile>
            </>
        );
    }

    return (
        <>
            <PaddedOnMobile>
                <VStack gap={4} maxW="600px">
                    <Flex direction="column" maxW="95%">
                        <Box
                            fontFamily="regular"
                            fontSize={scale(windowWidth, minSize, maxSize, 12, 24)}
                            color={EasierSectionData.labelColor}
                        >
                            {EasierSectionData.label}
                        </Box>
                        <Box
                            fontFamily="regular"
                            fontSize={scale(windowWidth, minSize, maxSize, 32, 55)}
                            lineHeight="110%"
                        >
                            {EasierSectionData.title.mobile}
                        </Box>
                        <Box
                            pt={2}
                            pb={4}
                            fontSize={scale(windowWidth, minSize, maxSize, 16, 24)}
                            fontWeight="light"
                        >
                            {EasierSectionData.subtitle}
                        </Box>
                    </Flex>

                    <Flex w="100%" align="end">
                        <MotionImage src={easierImage} {...scaleAnimation()} />
                    </Flex>

                    <Grid
                        templateColumns="auto 1fr"
                        templateRows="auto auto auto"
                        columnGap={2}
                        rowGap={8}
                    >
                        {EasierSectionData.bullets.map((bullet, index) => {
                            return (
                                <Fragment key={index}>
                                    <MotionGridItem {...slideUpAnimation(0.2)}>
                                        <Icon
                                            as={Image}
                                            src={bullet.icon}
                                            w={scale(windowWidth, minSize, maxSize, 60, 85) + "px"}
                                            h={scale(windowWidth, minSize, maxSize, 60, 85) + "px"}
                                        />
                                    </MotionGridItem>

                                    <MotionGridItem {...slideUpAnimation(0.2)}>
                                        <Flex direction="column" justify="center" h="100%" gap={1}>
                                            <Box
                                                fontFamily="regular"
                                                fontSize={scale(
                                                    windowWidth,
                                                    minSize,
                                                    maxSize,
                                                    18,
                                                    30,
                                                )}
                                                lineHeight="110%"
                                            >
                                                {bullet.title}
                                            </Box>
                                            <Box
                                                fontSize={scale(
                                                    windowWidth,
                                                    minSize,
                                                    maxSize,
                                                    16,
                                                    24,
                                                )}
                                                fontWeight="light"
                                                color="lavenderGray"
                                                lineHeight="110%"
                                            >
                                                {bullet.description}
                                            </Box>
                                            {bullet.comingSoon && (
                                                <Box
                                                    fontFamily="regular"
                                                    fontSize={scale(
                                                        windowWidth,
                                                        minSize,
                                                        maxSize,
                                                        10,
                                                        15,
                                                    )}
                                                    fontWeight="medium"
                                                    color="lavenderGray"
                                                    lineHeight="110%"
                                                >
                                                    Coming Soon
                                                </Box>
                                            )}
                                        </Flex>
                                    </MotionGridItem>
                                </Fragment>
                            );
                        })}
                    </Grid>

                    <MotionFlex pt={8} {...scaleAnimation(0.2)}>
                        <Box w="286px">
                            <GradientButton
                                link="/app"
                                label="View App"
                                // tag="BETA"
                            />
                        </Box>
                    </MotionFlex>
                </VStack>
            </PaddedOnMobile>
        </>
    );
};

export default EasierSection;
